<template>
  <div class="league">
    <v-form v-model="formValid">
      <v-container class="pt-0">
        <span class="headline">{{headline}}</span>
        <v-row>
          <v-col cols="12" sm="6">
            <game-select
              v-model="game"
              data-test-id="game"
              :disabled="loading"
              all-games
              :rules="validationRules.required"
              clearable
            />
          </v-col>
        </v-row>
        <v-row v-if="game">
          <v-col cols="12" md="12">
            <v-text-field
              v-model="name"
              data-test-id="name"
              :label="$t('common.name')"
              :disabled="loading"
              :rules="validationRules.required"
              autocomplete="off"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              :label="$t('common.competition-type')"
              v-model="type"
              data-test-id="gametype"
              :disabled="loading"
              :items="types"
              :rules="validationRules.required"
              item-value="type"
              clearable
            >
              <template v-slot:item="data">
                <v-icon class="mr-2">{{ getGameTypeIcon(data.item) }}</v-icon>
                {{ getGameTypeLabel(data.item) }}
              </template>

              <template v-slot:selection="data">
                <v-icon class="mr-2">{{ getGameTypeIcon(data.item) }}</v-icon>
                {{ getGameTypeLabel(data.item) }}
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="6">
            <platform-select
              data-test-id="platform"
              v-model="platform"
              :disabled="loading"
              :rules="validationRules.required"
            />
          </v-col>
          <v-col cols="12" sm="8" class="pt-0 pb-0">
            <v-select
              :label="$t('common.team-mode')"
              v-model="mode"
              data-test-id="gamemode"
              :disabled="loading"
              :items="modes"
              :rules="validationRules.required"
            >
              <template v-slot:item="data">
                <v-icon class="mr-2">{{ getGameModeIcon(data.item) }}</v-icon>
                {{ getGameModeLabel(data.item) }}
              </template>
              <template v-slot:selection="data">
                <v-icon class="mr-2">{{ getGameModeIcon(data.item) }}</v-icon>
                {{ getGameModeLabel(data.item) }}
              </template>
            </v-select>
          </v-col>
          <v-col cols="6" sm="4" v-if="gameModeIsAnyTeamMode(mode)" class="pt-0 pb-0">
            <v-select
              :label="$t('common.team-size')"
              v-model="teamSize"
              data-test-id="teamSize"
              :disabled="loading"
              :items="teamSizes"
              :rules="validationRules.required"
            >
              <template v-slot:item="data">
                {{$tc('common.num-players-format', data.item, {num: data.item})}}
              </template>
              <template v-slot:selection="data">
                {{$tc('common.num-players-format', data.item, {num: data.item})}}
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" v-if="creationWarningMessage">
            <alert :text="creationWarningMessage" :type="'warning'" :link="'/premium'" />
          </v-col>
        </v-row>
        <v-row v-if="game && isOrganisator()">
          <v-col cols="12" md="6" class="pb-0">
            {{$t('common.participation-fee')}}
            <v-radio-group v-model="participationFeeType" :disabled="loading">
              <v-radio
                :label="$t('common.cost-free')"
                :value="'free'"
              ></v-radio>
              <v-radio
                :label="'Coins'"
                :value="'coins'"
              ></v-radio>
            </v-radio-group>
            <v-row no-gutters v-if="participationFeeType == 'coins'" class="pl-8">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="participationCoinFee"
                  type="number"
                  :label="'Coins'"
                  :append-icon="'mdi-circle-multiple-outline'"
                  :rules="validationRules.coinRule"
                  :disabled="loading"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <info-block small class="help-text-small">
                {{$t('createGame.coin-participation-fee-fee', {percentage: this.participationFeeFeePercentage})}}
                </info-block>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-if="game && type == 'ko.classic'">
          <v-col cols="12">
            <prizes-summary-widget :prizes="prizes"/>
          </v-col>
          <v-col cols="12">
            <v-btn
              data-test-id="editPrizesButton"
              color="primary"
              @click.stop="editPrizesDialog = true"
              :disabled="loading"
            >
              <v-icon left>mdi-trophy</v-icon>
              <span>{{$t('createGame.edit-prizes')}}</span>
            </v-btn>
          </v-col>
          <prizes-dialog
            v-model="prizes"
            v-if="editPrizesDialog"
            :visible="editPrizesDialog"
            :closeAction="function() {editPrizesDialog = false}"
          />
        </v-row>
        <v-row v-if="game">
          <v-col cols="12" class="pb-0">
            <v-textarea
              v-model="description"
              name="input-description"
              :disabled="loading"
              :counter="25000"
              :label="$t('common.description')"
              hint
               :rules="validationRules.description"
            ></v-textarea>
          </v-col>
          <v-col cols="12" class="pt-0">
            <info-block small class="help-text-small">{{$t('createGame.description-description')}}</info-block>
          </v-col>
        </v-row>
        <v-row v-if="game">
          <v-col cols="12" sm="6">
            <v-btn
              data-test-id="inviteFriendsButton"
              color="primary"
              @click.stop="inviteFriendsDialog = true"
              :disabled="loading"
            >
              <v-icon left>mdi-account-multiple-plus</v-icon>
              <span class="iOSWorkaroundBlack" v-if="!friendsToInvite || friendsToInvite.length == 0">{{$t('common.invite-friends')}}</span>
              <span class="iOSWorkaroundBlack" v-else>{{$tc('createGame.num-friends-invited', friendsToInvite.length, {num: friendsToInvite.length})}}</span>
            </v-btn>
          </v-col>
          <friends-selector-dialog
            v-model="friendsToInvite"
            :visible="inviteFriendsDialog"
            :closeAction="function() {inviteFriendsDialog = false}"
          />
        </v-row>
        <v-row v-if="game && hasPermissionToAddFriendsDirectly()">
          <v-col cols="12" sm="6">
            <v-btn
              data-test-id="addFriendsButton"
              color="primary"
              @click.stop="friendsDialog = true"
              :disabled="loading"
            >
              <v-icon left>mdi-account-multiple-plus</v-icon>
              <span class="iOSWorkaroundBlack">{{addFriendsButtonText}}</span>
            </v-btn>
          </v-col>
          <friends-selector-dialog
            v-model="friendsToAdd"
            :visible="friendsDialog"
            :closeAction="function() {friendsDialog = false}"
          />
        </v-row>
        <v-row v-if="game && (!hasPermissionToCreateGameWithCheckin() || !hasPermissionToCreateKOGameWithThirdPlace()) && isPremiumFeatureEnabled()" no-gutters>
          <v-col>
            <info-block small class="help-text-small">{{$t('createGame.some-features-are-deactivated')}}</info-block>
          </v-col>
        </v-row>
        <div v-if="game">
          <v-row no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading || !hasPermissionToCreateGameWithCheckin()" v-model="checkInEnabled" :label="$t('createGame.check-in-mandatory')" />
            </v-col>
            <v-col cols="12" v-if="checkInEnabled">
              <div class="pl-8">
                <v-flex class="d-inline-flex">
                  <date-time-picker v-model="checkInAt" :label="$t('competition.checkinFromTime')" :disabled="loading"/>
                </v-flex>
                <br/>
                <info-block small class="help-text-small">{{$t('createGame.checkin-notice')}}</info-block>
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading || !hasPermissionToSetCompetitionStartDate()" v-model="setStartDate" :label="$t('createGame.start-time-checkbox')" />
            </v-col>
            <v-col cols="12" v-if="setStartDate">
              <div class="pl-8">
                <v-flex class="d-inline-flex">
                  <date-time-picker v-model="startsAt" :label="$t('competition.startDate')" :disabled="loading"/>
                </v-flex>
                <br/>
                <info-block small class="help-text-small">{{$t('createGame.start-time-notice')}}</info-block>
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading || !hasPermissionToCreateGameWithoutParticipating()" v-model="withoutParticipating" :label="$t('createGame.participate-as-spectator')" />
            </v-col>
          </v-row>
          <v-row v-if="hasPermissionToCreateGameForPremiumAccountsOnly()" no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading" v-model="premiumPlayersOnly" :label="$t('competition.premium-only')" />
            </v-col>
          </v-row>
          <v-row v-if="hasPermissionToCreateOfficialCompetition()" no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading" v-model="isOfficialCompetition" :label="$t('createGame.its-an-official-espocial-competition', {product: $tenant.title})" />
            </v-col>
          </v-row>
          <v-row v-if="hasPermissionToCreatePublicCompetition()" no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading" v-model="isPublicCompetition" :label="$t('createGame.publish-competition')" />
            </v-col>
            <v-col cols="12" v-if="isPublicCompetition">
              <portals-select v-model="portalsToPublish" :clearable="true" :disabled="loading"/>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12" v-if="hasPermissionToCreatePublicCompetition() === false">
              <v-checkbox :disabled="loading || !hasPermissionToCreatePublicUserCup()" v-model="isPublicUserCup" :label="$t('createGame.publish-in-usercups')" />
            </v-col>
          </v-row>
          <v-row v-if="this.type == 'league.classic'" no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading || !hasPermissionToCreateLeagueOneRound()" v-model="leagueOneRound" :label="$t('createGame.league-one-round')" />
            </v-col>
            <v-col cols="12" v-if="leagueOneRound || !hasPermissionToCreateLeagueOneRound()">
              <div class="pl-8">
                <info-block small class="help-text-small">{{$t('createGame.league-one-round-notice')}}</info-block>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="this.type == 'ko.classic'" no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading || !hasPermissionToCreateKOGameWithThirdPlace()" v-model="koPlayThirdPlace" :label="$t('createGame.play-third-place')" />
            </v-col>
            <v-col cols="12" v-if="koPlayThirdPlace || !hasPermissionToCreateKOGameWithThirdPlace()">
              <div class="pl-8">
                <info-block small class="help-text-small">{{$t('createGame.play-third-place-notice')}}</info-block>
              </div>
            </v-col>
          </v-row>
          <v-row v-if="this.type == 'ko.classic'" no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading || !hasPermissionToCreateKOGameWithRandomMatchOpponents()" v-model="koRandomizeMatchOpponents" :label="$t('createGame.ko-randomize-match-opponents')" />
            </v-col>
            <v-col cols="12" v-if="koRandomizeMatchOpponents || !hasPermissionToCreateKOGameWithRandomMatchOpponents()">
              <div class="pl-8">
                <info-block small class="help-text-small">{{$t('createGame.ko-randomize-match-opponents-notice')}}</info-block>
              </div>
            </v-col>
          </v-row>
          <v-row v-if="this.manualNextRoundAllowed" no-gutters>
            <v-col cols="12">
              <v-checkbox :disabled="loading || !hasPermissionToCreateGameWithManualNextRound()" v-model="manualNextRound" :label="$t('createGame.manual-next-round')" />
            </v-col>
            <v-col cols="12" v-if="manualNextRound">
              <div class="pl-8">
                <info-block small class="help-text-small">{{$t('createGame.manual-next-round-notice')}}</info-block>
              </div>
            </v-col>
          </v-row>
          <v-row v-if="hasPermissionToCreateGameWithRestrictedPlayerCount()">
            <v-col>
              <v-select
                :label="$t('common.maximum-players')"
                v-model="maxPlayers"
                data-test-id="maxPlayers"
                :disabled="loading"
                :items="this.type == 'ko.classic' ? maxPlayersValues : maxPlayersLeagueValues"
                clearable
              >
                <template v-slot:item="data">
                  {{ data.item === null ? $t('common.no-limit') : $tc('common.num-players-format', data.item, {num: data.item}) }}
                </template>

                <template v-slot:selection="data">
                  {{ data.item === null ? $t('common.no-limit') : $tc('common.num-players-format', data.item, {num: data.item}) }}
                </template>
              </v-select>
            </v-col>
          </v-row>
        </div>
        <v-row v-if="costStatement && costStatement.items.length > 0">
          <v-col cols="12" md="6" class="pt-0 pb-0">
            <coin-cost-statement :costStatement="costStatement"/>
          </v-col>
          <v-col cols="12" md="6" class="pt-0 pb-0">
            <info-block small class="help-text-small">{{$t('createGame.cost-statement-notice')}}</info-block>
          </v-col>
        </v-row>
        <v-row v-if="game">
          <v-col cols="12" class="text-left">
            <v-btn
              data-test-id="submitButton"
              :disabled="!formValid || calculationLoading > 0"
              :loading="isLoading || calculationLoading > 0"
              color="primary"
              @click="createGame"
            >
              <v-icon left>mdi-soccer-field</v-icon>
              <span class="iOSWorkaroundBlack" v-if="!costStatement || costStatement.total == 0">{{$t('createGame.create')}}</span>
              <span class="iOSWorkaroundBlack" v-else>{{$t('createGame.create-and-pay')}}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </div>
</template>

<script>
import {
  MUTATION_RAISE_NOTIFICATION,
  ACTION_RELOAD_ACTIVE_GAMES,
  MUTATION_PRESET_GAME,
  MUTATION_PRESET_PLATFORM
} from "@/plugins/store";
import FriendsSelectorDialog from "@/widgets/FriendsSelectorDialog";
import PlatformSelect from "@/widgets/PlatformSelect";
import DateTimePicker from "@/widgets/DateTimePicker";
import gameTypeMixin from "@/mixins/gameTypeMixin";
import gameModeMixin from "@/mixins/gameModeMixin"
import {GAME_MODE_SINGLEPLAYER, GAME_MODE_TEAM_COOP, GAME_MODE_TEAM_VBL} from "@/mixins/gameModeMixin"
import Alert from "@/widgets/Alert";
import envMixin from "@/mixins/envMixin";
import CoinCostStatement from '@/widgets/CoinCostStatement.vue';
import PortalsSelect from '@/widgets/PortalsSelect.vue';
import PrizesDialog from "@/widgets/prize/PrizesDialog";
import PrizesSummaryWidget from "@/widgets/prize/PrizesSummaryWidget";
import InfoBlock from '@/widgets/InfoBlock.vue';
import GameSelect from "@/widgets/GameSelect";

export default {
  name: "CreateGame",
  mixins: [gameTypeMixin, gameModeMixin, envMixin],
  components: {
    FriendsSelectorDialog,
    PlatformSelect,
    DateTimePicker,
    Alert,
    CoinCostStatement,
    PortalsSelect,
    PrizesDialog,
    PrizesSummaryWidget,
    InfoBlock,
    GameSelect,
  },
  props: {
    gameUID: String
  },
  data: function() {
    let maxPlayersValues = [null]
    let maxPlayersLeagueValues = [null]
    for (var i=2; i<=2048; i=i*2) {
      maxPlayersValues.push(i)
      if (i <= 64) {
        maxPlayersLeagueValues.push(i)
      }
    }
    return {
      costStatement: undefined,
      calculationLoading: 0,
      headline: this.$t('createGame.title'),
      types: [],
      type: undefined,  // e.g. "league.classic" or "ko.classic"
      mode: GAME_MODE_SINGLEPLAYER, // e.g. 1vs1, XvsX
      teamSize: 2,
      game: undefined,
      platform: undefined,
      name: undefined,
      description: undefined,
      loading: false,
      friendsDialog: false,
      friendsToAdd: [],
      inviteFriendsDialog: false,
      friendsToInvite: [],
      creationWarningMessage: undefined,
      withoutParticipating: false,
      premiumPlayersOnly: false,
      isOfficialCompetition: false,
      setStartDate: false,
      checkInEnabled: false,
      maxPlayers: null,
      maxPlayersValues: maxPlayersValues,
      maxPlayersLeagueValues: maxPlayersLeagueValues,
      isPublicCompetition: false,
      isPublicUserCup: false,
      portalsToPublish: [],
      startsAt: null,
      checkInAt: null,
      leagueOneRound: false,
      manualNextRound: false,
      koPlayThirdPlace: false,
      koRandomizeMatchOpponents: false,
      editPrizesDialog: false,
      /*
      prizes e.g. [
        {'place': 1, 'type': 'COINS', 'data': {'coins': 100}},
        {'place': 1, 'type': 'CUSTOM', 'data': {'text': '10 € PSN Gutschein'}},
      ]
      */
      prizes: [],
      participationFeeType: 'free',
      participationCoinFee: 0,
      ignoreRouteUpdates: false,
      formValid: false,
      validationRules: {
        description: [v => !v || v.length <= 25000 || this.$t('common.max-n-characters', {n: 25000})],
        required: [v => !!v || this.$t('common.mandatory-field')],
        coinRule: [v => parseInt(v) > 0 || '']
      }
    };
  },
  computed: {
    teamSizes(){
      if(this.gameModeIsVbl(this.mode)) {
        return [5];
      }

      if(this.gameTypeIsKoMixed(this.type)) {
        return [2];
      }
      return [2, 3, 4, 5, 6, 7, 8]
    },
    addFriendsButtonText() {
      if (!this.friendsToAdd || this.friendsToAdd.length == 0) {
        return this.$t('createGame.add-friends')
      } else if (this.friendsToAdd.length == 1) {
        return this.friendsToAdd.length + ' Freund wird hinzugefügt'
      }
      return this.friendsToAdd.length + ' Freunde werden hinzugefügt'
    },
    manualNextRoundAllowed() {
      return this.type == 'ko.classic';
    },
    isLoading() {
      return this.loading; // this.$store.state.isLoading
    },
    createCompetitionBasicData() {
      let createCompetitionData = {
        game: this.game,
        platform: this.platform,
        withoutParticipating: this.withoutParticipating,
        maxPlayers: this.maxPlayers,
        premiumOnly: this.premiumPlayersOnly,
        officialCompetition: this.isOfficialCompetition,
        publicCompetition: this.isPublicCompetition,
        checkInEnabled: this.checkInEnabled,
        portals: this.portalsToPublish,
        mode: this.mode,
      }
      if (this.gameModeIsAnyTeamMode(this.mode)) {
        createCompetitionData.teamSize = this.teamSize
      }
      if (this.setStartDate) {
        createCompetitionData.startsAt = (this.startsAt ? this.startsAt.toISOString() : null)
      }
      if (this.checkInEnabled) {
        createCompetitionData.checkInAt = (this.checkInAt ? this.checkInAt.toISOString() : null)
      }
      if (this.manualNextRoundAllowed) {
        createCompetitionData.manualNextRound = this.manualNextRound;
      }
      if (this.type == 'ko.classic') {
        createCompetitionData.prizes = this.prizes
        if (this.koPlayThirdPlace) {
          createCompetitionData.playThirdPlace = true;
        }
        createCompetitionData.tournamentTreeLogic = !this.koRandomizeMatchOpponents;
      } else if (this.type == 'league.classic') {
        createCompetitionData.oneRound = this.leagueOneRound;
      }

      createCompetitionData._dummy = this.formValid; // Force update if formValid changes
      if (this.participationFeeType == 'coins') {
        createCompetitionData.participationCoinFee = this.participationCoinFee;
      }
      return createCompetitionData;
    },
    participationFeeFeePercentage() {
      return this.envGetUserKey('coins.potFee') * 100;
    },
    modes() {
      // TODO: Remove when teammode is finished. See #1300
      if (!this.isSuperUser()) {
        return [GAME_MODE_SINGLEPLAYER];
      }
      if(this.type === 'league.classic'){
        return [GAME_MODE_SINGLEPLAYER, GAME_MODE_TEAM_COOP, GAME_MODE_TEAM_VBL];
      }

      if(this.type === 'ko.mixed'){
        return [GAME_MODE_TEAM_COOP];
      }

      // TBD: GAME_MODE_TEAM_XVSX
      return [GAME_MODE_SINGLEPLAYER, GAME_MODE_TEAM_COOP];
    }
  },
  watch: {
    teamSizes: function(newVal){
      this.teamSize = newVal[0]
    },
    type: function(newVal) {
      if (typeof newVal == 'object') { // We dont know where this object = [] comes from
        console.log('ignoring', newVal)
        return;
      }
      this.maxPlayers = undefined
      this.updateRoute()
      this.creationWarningMessage = undefined;
      if (newVal) {
        this.$api.checkGameCreation(newVal).then(res => {
          if (res.data.message === null) {
            this.creationWarningMessage = undefined;
          } else {
            this.creationWarningMessage = res.data.message;
          }
        });
      }
    },
    mode: function() {
      this.updateRoute()
    },
    platform: function() {
      this.updateRoute()
    },
    game: function() {
      this.type = undefined
      this.updateRoute()
      this.updateGameTypes()
    },
    createCompetitionBasicData: function() {
      // console.log('create competition data updated', data)
      this.computeCostStatement()
    }
  },
  mounted() {
    this.ignoreRouteUpdates = true;
    this.game = this.$store.state.presets.game;
    this.platform = this.$store.state.presets.platform;
    if (this.$route.query.type) {
      this.type = this.$route.query.type;
    }
    if (this.$route.query.mode) {
      this.mode = this.$route.query.mode;
    }
    if (this.$route.query.platform) {
      this.platform = this.$route.query.platform;
    }
    if (this.$route.query.game) {
      this.game = this.$route.query.game;
    }

    this.ignoreRouteUpdates = false;
  },
  methods: {
    reset() {
      this.friendsToAdd = [];
      this.name = undefined;
    },
    computeCostStatement() {
      // console.log('computeCostStatement ', this.formValid)
      if (!this.formValid) {
        this.costStatement = undefined;
        return;
      }
      let creationData = this.createCompetitionBasicData;
      if (!this.type || !creationData.game || !creationData.platform) {
        // console.log('stop computeCostStatement')
        this.costStatement = undefined;
        return;
      }
      creationData.name = 'Hidden';
      this.calculationLoading ++;
      this.$api.calculateGameInstanceCreation(this.type, creationData).then(res => {
        this.calculationLoading --;
        this.costStatement = res.data;
      })
      .catch(error => {
        this.calculationLoading --;
        this.costStatement = undefined;
        this.$common.onError(error)
      });
    },
    createGame() {
      if (this.setStartDate && !this.startsAt) {
        this.$store.commit(MUTATION_RAISE_NOTIFICATION, {
          message: this.$t('createGame.start-time-missing'),
          type: "warning"
        });
        return
      }
      this.loading = true;
      let friendUIDsToAdd = this.friendsToAdd.map(friend => {
        return friend.uid;
      });
      let friendUIDsToInvite = this.friendsToInvite.map(friend => {
        return friend.uid;
      });
      console.log("Creating game instance of type " + this.type);
      this.$store.commit(MUTATION_PRESET_GAME, this.game);
      this.$store.commit(MUTATION_PRESET_PLATFORM, this.platform);
      let createCompetitionData = this.createCompetitionBasicData;
      createCompetitionData.name = this.name;
      createCompetitionData.description = this.description;
      createCompetitionData.usersToAdd = friendUIDsToAdd;
      createCompetitionData.usersToInvite = friendUIDsToInvite;
      createCompetitionData.publicUserCup = this.isPublicUserCup;

      this.$api
        .createGameInstance(this.type, createCompetitionData)
        .then(res => {
          this.loading = false;
          // console.log(res);
          this.$store.commit(MUTATION_RAISE_NOTIFICATION, {
            message: this.$t('createGame.created-notification', {name: res.data.name}),
            type: "success"
          });
          let gameUID = res.data.uid;
            this.$router.push({ name: 'competition',
            params: { gameUID: gameUID },
            query: { tab: "participants" }
          });
          this.$store.dispatch(ACTION_RELOAD_ACTIVE_GAMES);
        })
        .catch(error => {
          this.loading = false;
          this.$common.onError(error)
        });
    },
    updateGameTypes() {
      if (!this.game) {
        this.type = [];
        return;
      }
      this.loading = true;
      this.$api
        .getGameOptions(this.game)
        .then(res => {
          this.loading = false;
          // console.log(res.data)
          this.types = res.data.gameModes;
        })
        .catch(error => {
          this.loading = false;
          this.$common.onError(error)
        });
    },
    updateRoute() {
      if (this.ignoreRouteUpdates) {
        return;
      }
      this.$router.replace({ query: { platform: this.platform, type: this.type, game: this.game, mode: this.mode } }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.inactive {
  color: #888;
}
</style>
